import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppService } from '@App';
import { ICaseWorkbenchTask } from '@CaseSummary/underwriting/models/uw-task.model';
import { IConfigService } from '@Config';
import { WorkbenchTask } from '@Models/config/workbench-task';
import { BaseService } from '@Services';
import { Observable, ReplaySubject } from 'rxjs';

@Injectable()
export class UWTaskService extends BaseService {
    baseUrl: string;
    publicApiBaseUrl: string;

    private caseWorkbenchTasksSub = new ReplaySubject<ICaseWorkbenchTask[]>(1);
    public caseWorkbenchTasks$ = this.caseWorkbenchTasksSub.asObservable();
    caseWorkbenchTasksLoaded = false;
    caseIdForLoadedCaseWorkbenchTasks = '';

    constructor(
        public appService: AppService,
        public httpClient: HttpClient,
        public configService: IConfigService
    ) {
        super(appService, configService, httpClient);

        this.baseUrl = this.appService.getAPIBaseURL();
        this.publicApiBaseUrl = this.configService.getConfiguration().publicApiUrl;
    }

    // Read - Get task templates
    getWorkbenchTasks(accountId: string, accountVersion: number) : Observable<WorkbenchTask[]> {
        const clientId = this.appService.getClientId('');
        return super.getData<WorkbenchTask[]>(`${this.publicApiBaseUrl}client/${clientId}/account/${accountId}/${accountVersion}/workbenchtasks`);
    }

    public loadCaseWorkbenchTasks(clientCode: string, caseId: string, reloadTasks: boolean = false) : Observable<ICaseWorkbenchTask[]> {
        if (this.caseWorkbenchTasksLoaded && this.caseIdForLoadedCaseWorkbenchTasks === caseId && !reloadTasks) return this.caseWorkbenchTasks$;

        const response = this.reloadCaseWorkbenchTasks(caseId);
        return response;
    }

    reloadCaseWorkbenchTasks(caseId: string, hideWaiting: boolean = false) {
        const clientId = this.appService.getClientId('');
        const url = `${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtasks`;

        const sub = super.getData<ICaseWorkbenchTask[]>(url, clientId, hideWaiting).subscribe({
            next: caseWorkbenchTasks => {
                this.caseWorkbenchTasksSub.next(caseWorkbenchTasks);
                this.caseWorkbenchTasksLoaded = true;
                this.caseIdForLoadedCaseWorkbenchTasks = caseId;
            },
            error: () => undefined,
            complete: () => {
                sub.unsubscribe();
            }
        });

        return this.caseWorkbenchTasks$;

    }

    // Read - Get a single UW task by ID
    getUWTask(caseId: string, taskId: string) : Observable<any> {
        const clientId = this.appService.getClientId('');
        return super.getData<any>(`${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}`);
    }

    // Create - Add a new UW task
    createUWTask(caseId: string, task: any) : Observable<any> {
        const clientId = this.appService.getClientId('');
        return super.postData<any>(`${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask`, task);
    }

    // Create - Add multiple UW tasks
    createUWTasks(caseId: string, tasks: ICaseWorkbenchTask[]) : Observable<any> {
        const clientId = this.appService.getClientId('');
        return super.postData<any>(`${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtasks`, tasks);
    }

    // Update - Modify an existing UW task
    updateUWTask(caseId: string, taskId: string, task: any) : Observable<any> {
        const clientId = this.appService.getClientId('') || task?.clientId;
        return super.putData<any>(`${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}`, task);
    }

    // Delete - Remove a UW task
    deleteUWTask(caseId: string, taskId: string) : Observable<any> {
        const clientId = this.appService.getClientId('');
        return super.deleteData<any>(`${this.publicApiBaseUrl}client/${clientId}/case/${caseId}/workbenchtask/${taskId}`);
    }
}
